/**
 * Calendar — main calendar component shell.
 * Renders month, week, day, and agenda views.
 * Header with period label, navigation, view toggle.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import { type Component } from '@/types';
import { navigateDate, type CalendarViewMode } from './calendarDateUtils';
import { type CalendarEvent, resolveEventColor } from './calendarEventPositioning';
import { CalendarMonthView } from './CalendarMonthView';
import { CalendarEventDetailModal } from './CalendarEventDetailModal';
import type { CalendarFieldMapping } from '@/types/pm-types';
import { useDataWriteback } from '@/hooks/useDataWriteback';
import { useAutomationRules } from '@/hooks/useAutomationRules';
import { useAuthStore } from '@/stores/auth/auth-store';
import { exportToCSV } from '@/utils/pmDataExport';
import { pmTheme } from './pmTheme';
import type { ComponentDataWriteOperation } from '@/types/componentDataBinding';
import { getDataBindingWriteTarget } from './dataBindingWriteTarget';
import {
  readAutomationRules,
  readCalendarFieldMapping,
  readCalendarViewMode,
  readNumber,
  readString,
  readStringRecord,
  readWritebackConfig,
} from './pmPropertyReaders';

// Lazy-load additional views (hoisted to module scope to avoid remounting on re-render)
const CalendarWeekView = React.lazy(() => import('./CalendarWeekView').then(m => ({ default: m.CalendarWeekView })));
const CalendarDayView = React.lazy(() => import('./CalendarDayView').then(m => ({ default: m.CalendarDayView })));
const CalendarAgendaView = React.lazy(() => import('./CalendarAgendaView').then(m => ({ default: m.CalendarAgendaView })));

interface CalendarProps {
  component: Component;
  data: Record<string, unknown>[];
  isViewerMode: boolean;
  writeTargetPermissions?: Partial<Record<ComponentDataWriteOperation, boolean>>;
  writeTargetRequestingOrganizationId?: string | null;
}

const VIEW_LABELS: Record<CalendarViewMode, string> = {
  month: 'Month',
  week: 'Week',
  day: 'Day',
  agenda: 'Agenda',
};
const CALENDAR_VIEW_MODES: CalendarViewMode[] = ['month', 'week', 'day', 'agenda'];

/** Map raw data records to CalendarEvent[] using field mapping */
function mapDataToEvents(
  data: Record<string, unknown>[],
  fieldMapping: CalendarFieldMapping | Record<string, string | undefined>,
): CalendarEvent[] {
  if (!Array.isArray(data)) return [];
  return data.map((record, index) => {
    const getField = (fm: Record<string, string | undefined>, role: string) => fm[role];

    const idField = getField(fieldMapping, 'id') || 'id';
    const titleField = getField(fieldMapping, 'title') || 'title';
    const startField = getField(fieldMapping, 'startDate') || 'start';
    const endField = getField(fieldMapping, 'endDate') || 'end';
    const allDayField = getField(fieldMapping, 'allDay');
    const categoryField = getField(fieldMapping, 'category');
    const colorField = getField(fieldMapping, 'color');
    const descField = getField(fieldMapping, 'description');
    const locationField = getField(fieldMapping, 'location');
    const assigneeField = getField(fieldMapping, 'assignee');

    return {
      id: String(record[idField] ?? `event-${index}`),
      title: String(record[titleField] ?? ''),
      start: String(record[startField] ?? ''),
      end: record[endField] ? String(record[endField]) : undefined,
      allDay: allDayField ? Boolean(record[allDayField]) : undefined,
      category: categoryField ? String(record[categoryField] ?? '') : undefined,
      color: colorField ? String(record[colorField] ?? '') : undefined,
      description: descField ? String(record[descField] ?? '') : undefined,
      location: locationField ? String(record[locationField] ?? '') : undefined,
      assignee: assigneeField ? String(record[assigneeField] ?? '') : undefined,
    };
  }).filter(e => e.start); // Filter out events with no start date
}

function getPeriodLabel(date: Date, viewMode: CalendarViewMode): string {
  switch (viewMode) {
    case 'month':
      return format(date, 'MMMM yyyy');
    case 'week':
      return `Week of ${format(date, 'MMM d, yyyy')}`;
    case 'day':
      return format(date, 'EEEE, MMMM d, yyyy');
    case 'agenda':
      return format(date, 'MMMM yyyy');
    default:
      return format(date, 'MMMM yyyy');
  }
}

export const Calendar: React.FC<CalendarProps> = ({
  component,
  data,
  isViewerMode,
  writeTargetPermissions,
  writeTargetRequestingOrganizationId,
}) => {
  const properties = component.properties;

  const initialViewMode = readCalendarViewMode(properties.viewMode);
  const firstDayOfWeek = readNumber(properties.firstDayOfWeek, 1);
  const showWeekends = properties.showWeekends !== false;
  const showWeekNumbers = Boolean(properties.showWeekNumbers);
  const businessHoursStart = readNumber(properties.businessHoursStart, 8);
  const businessHoursEnd = readNumber(properties.businessHoursEnd, 18);
  const timeSlotInterval = readNumber(properties.timeSlotInterval, 60);
  const maxEventsPerDay = readNumber(properties.maxEventsPerDay, 3);
  const showEventTime = properties.showEventTime !== false;
  const fieldMapping = useMemo(
    () => readCalendarFieldMapping(properties.fieldMapping),
    [properties.fieldMapping],
  );
  const colorMap = readStringRecord(properties.colorMap);
  const title = readString(properties.title, 'Calendar');
  const showExportButton = Boolean(properties.showExportButton);
  const writeback = readWritebackConfig(properties.writeback);
  const dataBindingWriteTarget = useMemo(
    () => getDataBindingWriteTarget(properties.dataBinding),
    [properties.dataBinding],
  );

  const {
    updateRecord,
    deleteRecord,
    writeTargetResolution,
    resolveWriteTarget,
  } = useDataWriteback({
    writebackConfig: writeback,
    writeTarget: dataBindingWriteTarget.writeTarget,
    requireExplicitWriteTarget:
      dataBindingWriteTarget.requireExplicitWriteTarget,
    writePermissions: writeTargetPermissions,
    writeTargetRequestingOrganizationId,
    componentId: component.id,
  });

  // Automation rules
  const user = useAuthStore((s) => s.user);
  const { handleDataEvent, detectNewRecords } = useAutomationRules({
    rules: readAutomationRules(properties.automationRules),
    writebackConfig: writeback,
    componentId: component.id,
    fieldMapping,
    currentUser: user?.firstName ?? user?.username,
    enabled: isViewerMode,
  });

  // Detect new records for onItemAdd automation rules
  useEffect(() => {
    if (isViewerMode) detectNewRecords(data);
  }, [data, isViewerMode, detectNewRecords]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<CalendarViewMode>(initialViewMode);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; event: CalendarEvent } | null>(null);

  const events = useMemo(() => mapDataToEvents(data, fieldMapping), [data, fieldMapping]);

  const handleExport = useCallback(() => {
    const visibleFields = Object.keys(fieldMapping).filter(k => fieldMapping[k]);
    exportToCSV(data, fieldMapping, visibleFields, `${title.replace(/\s+/g, '_')}_events`);
  }, [data, fieldMapping, title]);

  const handleNavigate = useCallback((direction: number) => {
    setCurrentDate(prev => navigateDate(prev, viewMode, direction));
  }, [viewMode]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleViewChange = useCallback((newView: CalendarViewMode) => {
    setViewMode(newView);
  }, []);

  const handleEventClick = useCallback((event: CalendarEvent) => {
    if (isViewerMode) {
      setSelectedEvent(event);
    }
  }, [isViewerMode]);

  const handleEventHover = useCallback((e: React.MouseEvent, event: CalendarEvent) => {
    if (isViewerMode) {
      setTooltip({ x: e.clientX, y: e.clientY, event });
    }
  }, [isViewerMode]);

  const handleEventSave = useCallback(async (eventId: string, updates: Record<string, unknown>) => {
    if (writeTargetResolution.status !== 'ready') return;
    // Map edited fields back through fieldMapping to data-source field names
    const fieldChanges: Record<string, unknown> = {};
    if (updates.title !== undefined && fieldMapping.title) {
      fieldChanges[fieldMapping.title] = updates.title;
    }
    if (updates.description !== undefined && fieldMapping.description) {
      fieldChanges[fieldMapping.description] = updates.description;
    }
    if (updates.location !== undefined && fieldMapping.location) {
      fieldChanges[fieldMapping.location] = updates.location;
    }
    if (Object.keys(fieldChanges).length > 0) {
      await updateRecord(eventId, fieldChanges);

      // Fire automation rules for each changed field
      const rawRecord = data.find((row) => String(row[fieldMapping.id ?? 'id']) === eventId);
      if (rawRecord) {
        for (const [dataField, newValue] of Object.entries(fieldChanges)) {
          handleDataEvent({
            type: 'onFieldChange',
            record: { ...rawRecord, ...fieldChanges },
            previousRecord: rawRecord,
            field: dataField,
            oldValue: rawRecord[dataField],
            newValue,
          }).catch(console.error);
        }
      }
    }
    setSelectedEvent(null);
  }, [writeTargetResolution.status, fieldMapping, updateRecord, data, handleDataEvent]);

  const handleEventDelete = useCallback(async (eventId: string) => {
    if (resolveWriteTarget('delete').status !== 'ready') return;
    await deleteRecord(eventId);
    setSelectedEvent(null);
  }, [deleteRecord, resolveWriteTarget]);

  const handleEventHoverEnd = useCallback(() => {
    setTooltip(null);
  }, []);

  const renderView = () => {
    const commonProps = {
      currentDate,
      events,
      firstDayOfWeek,
      showWeekends,
      colorMap,
      isViewerMode,
      onEventClick: handleEventClick,
      onEventHover: handleEventHover,
      onEventHoverEnd: handleEventHoverEnd,
    };

    switch (viewMode) {
      case 'week':
        return (
          <React.Suspense fallback={<div style={{ padding: 20, textAlign: 'center', color: pmTheme.textSubtle }}>Loading...</div>}>
            <CalendarWeekView
              {...commonProps}
              businessHoursStart={businessHoursStart}
              businessHoursEnd={businessHoursEnd}
              timeSlotInterval={timeSlotInterval}
              showEventTime={showEventTime}
            />
          </React.Suspense>
        );
      case 'day':
        return (
          <React.Suspense fallback={<div style={{ padding: 20, textAlign: 'center', color: pmTheme.textSubtle }}>Loading...</div>}>
            <CalendarDayView
              {...commonProps}
              businessHoursStart={businessHoursStart}
              businessHoursEnd={businessHoursEnd}
              timeSlotInterval={timeSlotInterval}
              showEventTime={showEventTime}
            />
          </React.Suspense>
        );
      case 'agenda':
        return (
          <React.Suspense fallback={<div style={{ padding: 20, textAlign: 'center', color: pmTheme.textSubtle }}>Loading...</div>}>
            <CalendarAgendaView {...commonProps} />
          </React.Suspense>
        );
      case 'month':
      default:
        return (
          <CalendarMonthView
            {...commonProps}
            showWeekNumbers={showWeekNumbers}
            maxEventsPerDay={maxEventsPerDay}
            showEventTime={showEventTime}
          />
        );
    }
  };

  return (
    <div
      data-testid="calendar-container"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: pmTheme.surface,
        color: pmTheme.text,
        borderRadius: '8px',
        overflow: 'hidden',
        fontFamily: 'inherit',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          borderBottom: `1px solid ${pmTheme.border}`,
          flexShrink: 0,
        }}
      >
        {/* Left: Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            data-testid="calendar-nav-prev"
            onClick={() => handleNavigate(-1)}
            style={{
              border: `1px solid ${pmTheme.input}`,
              borderRadius: '6px',
              padding: '4px 8px',
              backgroundColor: pmTheme.surface,
              cursor: 'pointer',
              fontSize: '14px',
              color: pmTheme.text,
              lineHeight: 1,
            }}
            aria-label="Previous period"
          >
            &#8249;
          </button>
          <button
            data-testid="calendar-nav-today"
            onClick={handleToday}
            style={{
              border: `1px solid ${pmTheme.input}`,
              borderRadius: '6px',
              padding: '4px 10px',
              backgroundColor: pmTheme.surface,
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
              color: pmTheme.text,
            }}
          >
            Today
          </button>
          <button
            data-testid="calendar-nav-next"
            onClick={() => handleNavigate(1)}
            style={{
              border: `1px solid ${pmTheme.input}`,
              borderRadius: '6px',
              padding: '4px 8px',
              backgroundColor: pmTheme.surface,
              cursor: 'pointer',
              fontSize: '14px',
              color: pmTheme.text,
              lineHeight: 1,
            }}
            aria-label="Next period"
          >
            &#8250;
          </button>
          <span style={{ fontSize: '15px', fontWeight: 600, color: pmTheme.text, marginLeft: 8 }}>
            {getPeriodLabel(currentDate, viewMode)}
          </span>
        </div>

        {/* Right: View toggle + Export */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isViewerMode && (
            <div style={{ display: 'flex', gap: 2, backgroundColor: pmTheme.surfaceMuted, borderRadius: '6px', padding: 2 }}>
              {CALENDAR_VIEW_MODES.map(v => (
                <button
                  key={v}
                  data-testid={`calendar-view-${v}`}
                  onClick={() => handleViewChange(v)}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    fontWeight: viewMode === v ? 600 : 400,
                    color: viewMode === v ? pmTheme.textInverse : pmTheme.textMuted,
                    backgroundColor: viewMode === v ? pmTheme.primary : 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {VIEW_LABELS[v]}
                </button>
              ))}
            </div>
          )}
          {isViewerMode && showExportButton && (
            <button
              data-testid="calendar-export-csv"
              onClick={handleExport}
              style={{
                border: `1px solid ${pmTheme.input}`,
                borderRadius: '6px',
                padding: '4px 10px',
                backgroundColor: pmTheme.surface,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
                color: pmTheme.text,
              }}
              aria-label="Export events to CSV"
            >
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Calendar body */}
      <div style={{ flex: 1, overflow: 'auto' }} role="grid" aria-label={`${title} - ${viewMode} view`}>
        {renderView()}
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <CalendarEventDetailModal
          event={selectedEvent}
          colorMap={colorMap}
          writebackEnabled={
            writeTargetResolution.status === 'ready' ||
            resolveWriteTarget('delete').status === 'ready'
          }
          onClose={() => setSelectedEvent(null)}
          onSave={handleEventSave}
          onDelete={handleEventDelete}
        />
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x + 10,
            top: tooltip.y + 10,
            backgroundColor: 'hsl(var(--popover))',
            color: 'hsl(var(--popover-foreground))',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            zIndex: 9999,
            maxWidth: 250,
            pointerEvents: 'none',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
          }}
        >
          <div style={{ fontWeight: 600 }}>{tooltip.event.title || '(No title)'}</div>
          {tooltip.event.start && (
            <div style={{ opacity: 0.8, fontSize: '11px', marginTop: 2 }}>
              {tooltip.event.start.includes('T')
                ? format(new Date(tooltip.event.start), 'MMM d, h:mm a')
                : format(new Date(tooltip.event.start), 'MMM d')}
              {tooltip.event.end && ` — ${tooltip.event.end.includes('T')
                ? format(new Date(tooltip.event.end), 'h:mm a')
                : format(new Date(tooltip.event.end), 'MMM d')}`}
            </div>
          )}
          {!!tooltip.event.location && (
            <div style={{ opacity: 0.7, fontSize: '11px', marginTop: 2 }}>{String(tooltip.event.location)}</div>
          )}
          {!!tooltip.event.category && (
            <div style={{ marginTop: 4 }}>
              <span
                style={{
                  backgroundColor: resolveEventColor(tooltip.event.category, colorMap),
                  color: pmTheme.textInverse,
                  padding: '1px 6px',
                  borderRadius: '3px',
                  fontSize: '10px',
                }}
              >
                {tooltip.event.category}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Calendar;
